import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Navigate, Outlet, useOutletContext } from 'react-router';

import { useTypedParams } from '@sorare/routing';
import type { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { ANY_SPORT_HOME } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import { FixtureTabLayout } from '#src/layouts/FixtureLayout/FixtureTabLayout/index.tsx';

import type {
  ChampionshipLayoutQuery,
  ChampionshipLayoutQueryVariables,
} from './__generated__/layout.graphql.ts';
import type { PageParams } from './__generated__/routeParams.ts';

const CHAMPIONSHIP_LAYOUT_QUERY = gql`
  query ChampionshipLayoutQuery($sport: Sport!) {
    so5 {
      so5Fixture(type: MY_ONGOING, sport: $sport) {
        slug
      }
      universalSo5UserGroups(first: 1, sport: $sport) {
        nodes {
          slug
        }
      }
    }
  }
` as TypedDocumentNode<
  ChampionshipLayoutQuery,
  ChampionshipLayoutQueryVariables
>;

export const loader = queryLoader<PageParams>(
  CHAMPIONSHIP_LAYOUT_QUERY,
  ({ sport }) => ({
    sport,
  })
);

export const useChampionshipLayoutData = () => {
  return useOutletContext<{
    so5FixtureSlug: string;
    so5UserGroupSlug: string;
  }>();
};

const ChampionshipLayout = () => {
  const {
    flags: {
      enableChampionshipPerSport = {
        nba: false,
        baseball: false,
        football: false,
      },
    },
  } = useFeatureFlags();
  const { eventType } = useTypedParams<PageParams>();
  const { data } = useQueryLoader<ChampionshipLayoutQuery>();
  const { sport, generateSportContextPath } = useSportContext();
  const { so5Fixture, universalSo5UserGroups } = data?.so5 || {};

  if (!enableChampionshipPerSport[sport.toLowerCase()]) {
    return <Navigate to={generateSportContextPath(ANY_SPORT_HOME)} replace />;
  }

  return (
    <FixtureTabLayout
      so5FixtureSlug={so5Fixture?.slug}
      eventType={eventType.toUpperCase() as So5FixtureEvent}
    >
      <Outlet
        context={{
          so5FixtureSlug: so5Fixture?.slug,
          so5UserGroupSlug: universalSo5UserGroups?.nodes?.[0]?.slug,
        }}
      />
    </FixtureTabLayout>
  );
};

export default ChampionshipLayout;
